import React, { useState } from 'react';
import { 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText,
  CircularProgress
} from '@mui/material';
import { Translate as TranslateIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

/**
 * Secure OAuth2/OIDC integration for TranslatorUI
 * Implements PKCE flow for maximum security
 */
const TranslatorUILink = () => {
  const [loading, setLoading] = useState(false);
  const { user, getIdToken } = useAuth();
  
  // OAuth2 configuration
  const TRANSLATOR_UI_URL = 'https://translatorui.myviewonline.com';
  const OAUTH_AUTHORIZE_URL = `${process.env.REACT_APP_API_URL}/api/v1/translator/authorize`;
  const CLIENT_ID = 'translator-ui';
  const REDIRECT_URI = `${TRANSLATOR_UI_URL}/callback`;
  const SCOPE = 'openid profile email';
  
  /**
   * Generate cryptographically secure random string
   */
  const generateRandomString = (length) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return Array.from(array, byte => chars[byte % chars.length]).join('');
  };
  
  /**
   * Generate PKCE code challenge from verifier
   */
  const generateCodeChallenge = async (verifier) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    const base64 = btoa(String.fromCharCode(...new Uint8Array(digest)));
    // Convert to base64url
    return base64
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  };
  
  /**
   * Initiate OAuth2 authorization flow with PKCE
   */
  const handleTranslatorUIAccess = async () => {
    try {
      setLoading(true);
      
      // Get current user's token for authorization
      const idToken = await getIdToken();
      
      if (!idToken) {
        console.error('No authentication token available');
        return;
      }
      
      // Generate PKCE parameters
      const state = generateRandomString(32);
      const codeVerifier = generateRandomString(128);
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      
      // Store PKCE verifier and state in sessionStorage for the callback
      // TranslatorUI will need to retrieve these after redirect
      const authData = {
        state,
        codeVerifier,
        timestamp: Date.now(),
        userEmail: user?.email
      };
      
      // Encrypt and store auth data
      const encryptedData = btoa(JSON.stringify(authData));
      sessionStorage.setItem('translator_auth', encryptedData);
      
      // Build authorization URL with all required parameters
      const authParams = new URLSearchParams({
        response_type: 'code',
        client_id: CLIENT_ID,
        redirect_uri: REDIRECT_URI,
        state: state,
        scope: SCOPE,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        // Additional security parameters
        nonce: generateRandomString(32),
        prompt: 'none', // Don't prompt for login since user is already authenticated
        max_age: '300' // Require recent authentication (5 minutes)
      });
      
      const authUrl = `${OAUTH_AUTHORIZE_URL}?${authParams.toString()}`;
      
      // Create a form to POST the authorization request with the bearer token
      // This is more secure than passing token in URL
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = authUrl;
      form.target = '_blank';
      
      // Add authorization header via hidden field (backend will extract)
      const tokenField = document.createElement('input');
      tokenField.type = 'hidden';
      tokenField.name = 'authorization';
      tokenField.value = `Bearer ${idToken}`;
      form.appendChild(tokenField);
      
      // Add CSRF token
      const csrfField = document.createElement('input');
      csrfField.type = 'hidden';
      csrfField.name = 'csrf_token';
      csrfField.value = generateRandomString(32);
      form.appendChild(csrfField);
      
      document.body.appendChild(form);
      
      // Open in new window with specific features for security
      const translatorWindow = window.open(
        '', 
        'TranslatorUI',
        'width=1200,height=800,menubar=no,toolbar=no,location=no,status=no'
      );
      
      if (translatorWindow) {
        form.target = 'TranslatorUI';
        form.submit();
        
        // Clean up form
        document.body.removeChild(form);
        
        // Set up message listener for the callback
        const messageHandler = (event) => {
          // Verify origin
          if (event.origin !== TRANSLATOR_UI_URL) {
            return;
          }
          
          // Handle authentication complete message
          if (event.data?.type === 'auth_complete') {
            const storedAuth = sessionStorage.getItem('translator_auth');
            if (storedAuth) {
              const authData = JSON.parse(atob(storedAuth));
              
              // Verify state matches
              if (event.data.state === authData.state) {
                console.log('TranslatorUI authentication successful');
                
                // Clean up
                sessionStorage.removeItem('translator_auth');
                window.removeEventListener('message', messageHandler);
              } else {
                console.error('State mismatch - possible CSRF attack');
              }
            }
          }
        };
        
        window.addEventListener('message', messageHandler);
        
        // Clean up listener after 5 minutes
        setTimeout(() => {
          window.removeEventListener('message', messageHandler);
          sessionStorage.removeItem('translator_auth');
        }, 5 * 60 * 1000);
        
      } else {
        // Fallback: If popup blocked, use direct navigation
        console.warn('Popup blocked, using direct navigation');
        
        // For direct navigation, we need to use GET with authorization header
        // This is less secure but necessary if popups are blocked
        window.open(authUrl, '_blank');
      }
      
    } catch (error) {
      console.error('Failed to initiate TranslatorUI access:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Alternative: Simpler one-time token approach (if OAuth2 is too complex)
  const handleSimpleTokenAccess = async () => {
    try {
      setLoading(true);
      
      // Get current user's token
      const idToken = await getIdToken();
      
      if (!idToken) {
        console.error('No authentication token available');
        return;
      }
      
      // Request one-time token from backend
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/translator/one-time-token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          purpose: 'translator_ui_access',
          expiry: 30 // 30 seconds
        })
      });
      
      if (response.ok) {
        const { token, expires_at } = await response.json();
        
        // Open TranslatorUI with one-time token
        const translatorUrl = `${TRANSLATOR_UI_URL}/auth?token=${token}&expires=${expires_at}`;
        window.open(translatorUrl, '_blank');
      } else {
        console.error('Failed to get one-time token');
      }
      
    } catch (error) {
      console.error('Failed to access TranslatorUI:', error);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <ListItem disablePadding>
      <ListItemButton 
        onClick={handleSimpleTokenAccess} // Use simple token by default for now
        disabled={loading}
      >
        <ListItemIcon>
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            <TranslateIcon />
          )}
        </ListItemIcon>
        <ListItemText 
          primary="Translator UI" 
          secondary={loading ? "Authenticating..." : "Secure Access"}
        />
      </ListItemButton>
    </ListItem>
  );
};

export default TranslatorUILink;